import { take } from 'lib/array/arrayUtils'
import type { OfferBestPriceData } from 'selectors/offerSelectors'
import { isBedbankOffer } from 'lib/offer/offerTypes'
import { isCalendarYmalOffer } from './calendarYmalOffer'

export const MAX_CALENDAR_SIMILAR_DISPLAY_COUNT = 6
export const MAX_CALENDAR_SIMILAR_CANDIDATE_COUNT = 18

interface Params {
  candidateOfferIds: Array<string>
  allOffers: Record<string, App.AnyOffer>
  offerErrors: App.OfferState['offerErrors']
  hasExactCheckIn: boolean
  bestPrices: Record<string, OfferBestPriceData>
  canUseHotelMemberPrice?: boolean
  canUseBedbankMemberPrice?: boolean
  maxCount?: number
}

function hasVisibleDatedPrice(
  offer: App.HotelOffer | App.BedbankOffer,
  prices: OfferBestPriceData,
  canUseHotelMemberPrice: boolean,
  canUseBedbankMemberPrice: boolean,
) {
  if ((prices.bestPrice ?? 0) > 0) {
    return true
  }

  const canUseMemberPrice = isBedbankOffer(offer) ? canUseBedbankMemberPrice : canUseHotelMemberPrice
  return canUseMemberPrice && (prices.bestMemberPrice ?? 0) > 0
}

export function getCalendarSimilarDisplayOfferIds({
  candidateOfferIds,
  allOffers,
  offerErrors,
  hasExactCheckIn,
  bestPrices,
  canUseHotelMemberPrice = false,
  canUseBedbankMemberPrice = false,
  maxCount = MAX_CALENDAR_SIMILAR_DISPLAY_COUNT,
}: Params): { displayOfferIds: Array<string>, pendingCount: number } {
  const readyIds: Array<string> = []
  let pending = 0

  for (const id of candidateOfferIds) {
    if (readyIds.length >= maxCount) {
      break
    }
    if (offerErrors[id]) {
      continue
    }
    const offer = allOffers[id]
    if (!offer) {
      pending += 1
      continue
    }
    if (!isCalendarYmalOffer(offer)) {
      continue
    }
    if (hasExactCheckIn) {
      const prices = bestPrices[id]
      if (!prices || prices.loading) {
        pending += 1
        continue
      }
      if (!hasVisibleDatedPrice(offer, prices, canUseHotelMemberPrice, canUseBedbankMemberPrice)) {
        continue
      }
    }
    readyIds.push(id)
  }

  return {
    displayOfferIds: take(readyIds, maxCount),
    pendingCount: pending,
  }
}
